import React, { useState } from 'react';
import {
  Box, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { OralExamRunStateResponseSupervisorBroadcast } from '@/common/api/models/oralExamRunStateResponseSupervisorBroadcast';

export type SupervisorBroadcastBannerProps = {
  broadcast: OralExamRunStateResponseSupervisorBroadcast;
};

/**
 * Student-facing banner for a supervisor's cohort broadcast (P8c).
 *
 * Dismissal is a client concern: we hide the banner once the student discards
 * the current message (keyed by `issuedAt`) and re-show it only when the
 * supervisor sends a newer one. This keeps an important "bear with me" message
 * visible until acknowledged without needing backend read receipts.
 */
export function SupervisorBroadcastBanner({ broadcast }: SupervisorBroadcastBannerProps) {
  const { t } = useTranslation();
  const [dismissedIssuedAt, setDismissedIssuedAt] = useState<string | null>(null);

  if (!broadcast) return null;
  if (broadcast.issuedAt === dismissedIssuedAt) return null;

  return (
    <Box
      bg="$bgBrandSubtle"
      borderBottomWidth={1}
      borderColor="$borderLight"
      px="$4"
      py="$3"
      testID="oral-exam-supervisor-broadcast"
    >
      <HStack space="sm" alignItems="flex-start" justifyContent="space-between">
        <VStack flex={1} space="xs">
          <Text size="xs" fontWeight="bold" color="$textOnBrandSubtle" textTransform="uppercase">
            {t('oralExam.supervisorMessageLabel', {
              defaultValue: 'Message from your supervisor',
            })}
          </Text>
          <Text size="sm" color="$textBody" testID="oral-exam-supervisor-broadcast-text">
            {broadcast.text}
          </Text>
        </VStack>
        <Pressable
          onPress={() => setDismissedIssuedAt(broadcast.issuedAt)}
          accessibilityRole="button"
          accessibilityLabel={t('common.buttons.dismiss', { defaultValue: 'Dismiss' })}
          testID="oral-exam-supervisor-broadcast-dismiss"
        >
          <Icon as={X} size="sm" color="$textSubtle" />
        </Pressable>
      </HStack>
    </Box>
  );
}
